import React, { useEffect, useRef } from "react";
import { AgGridReact, AgGridReactProps, AgReactUiProps } from "ag-grid-react";
import Config from "../../config/Config";
import "ag-grid-enterprise";
import { useAppDispatch } from "../../utils/hooks/hooks";

interface AgGridWrapperProps extends AgReactUiProps, AgGridReactProps {
  type?: "serverSide" | "clientSide";
  onGridReadyWithDispatch?: any;
}

/**
 * Renders a wrapper component for the AgGrid React component for use in listing the 
 * records in the module and in import module.
 *
 * @param {Object} props - The props object containing the following properties:
 *   - children: ReactNode - The child components to render within the AgGridWrapper.
 *   - type: "serverSide" | "clientSide" - The type of grid to render. Defaults to "clientSide".
 *   - context: Object - The context object to pass to the AgGridReact component.
 *   - onGridReadyWithDispatch: Function - The function to call when the grid is ready.
 *   - paginationPageSize: number - The number of rows to display per page.
 *   - rowData: any[] - The data to display in the grid.
 *   - rest: Object - Additional props to pass to the AgGridReact component.
 * @return {JSX.Element} The AgGridWrapper component.
 */
const AgGridWrapper: React.FC<AgGridWrapperProps> = ({
  children,
  type = "clientSide",
  context,
  onGridReadyWithDispatch,
  paginationPageSize,
  rowData,
  ...rest
}) => {
  const gridType =
    type === "serverSide" ? Config.grid.server : Config.grid.local;
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  useEffect(() => {
    gridRef.current.api?.setRowData(rowData);
    gridRef.current.api?.paginationSetPageSize(paginationPageSize);
  }, [rowData, paginationPageSize]);

  return (
    <div className="ag-theme-alpine ag-grid-wrapper">
      <AgGridReact
        ref={gridRef}
        {...gridType}
        {...(type === "serverSide" && onGridReadyWithDispatch
          ? {
              onGridReady: (params: any) => {
                params.api.showLoadingOverlay();
                dispatch(onGridReadyWithDispatch(params));
              },
            }
          : {
              onGridReady: (params: any) => {
                params.api.showLoadingOverlay();
              },
            })}
        rowData={rowData}
        paginationPageSize={paginationPageSize}
        loadingOverlayComponent={"agLoadingOverlay"}
        context={
          context && {
            ...context,
          }
        }
        {...rest}
      >
        {children}
      </AgGridReact>
    </div>
  );
};

export default AgGridWrapper;
